/**
 * CONFIGURAÇÕES › ORDENS DE SERVIÇO › TÉCNICOS DE CAMPO — só o admin (criar usuário alcança quem entra no sistema).
 *
 * Matheus, 25/10/2026: *"nem todos os usuários serão possiveis logar com email da @igmsecurity.com.br precisamos ...
 * criar um login e senha para esses casos"* — e as decisões dele: e-mail pessoal, só o aplicativo do técnico, link de
 * convite. Aqui o admin convida (nome + e-mail pessoal), vê quem já criou a senha e reenvia o convite de quem não criou.
 * Desativar é em Configurações › Usuários, como todo usuário.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';

import { Badge, Button, ErroCampo, Input, Label } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';

import { mensagemDoErro, osApi, type TecnicoDeCampo } from './os-api';

const ROTULO: Record<
  TecnicoDeCampo['situacao'],
  { texto: string; tom: 'success' | 'warning' | 'neutral' }
> = {
  ativo: { texto: 'Já entra', tom: 'success' },
  // O fato que o dado tem é "sem senha": se o e-mail não saiu, "convite enviado" seria mentira.
  convidado: { texto: 'Ainda não criou a senha', tom: 'warning' },
  desativado: { texto: 'Desativado', tom: 'neutral' },
};

/** `aoMudar`: o convite põe o técnico no departamento Técnico — a lista de técnicos do piloto, ao lado, recarrega. */
export function TecnicosDeCampo({ aoMudar }: { aoMudar?: () => void }) {
  const { show } = useToast();
  const [lista, setLista] = useState<TecnicoDeCampo[] | null>(null);
  const [abrindo, setAbrindo] = useState(false);
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [erros, setErros] = useState<{ nome?: string; email?: string }>({});
  const [ocupado, setOcupado] = useState(false);
  // Leitura que falha não é lista vazia (auditoria de 25/10/2026): dizia "Nenhum técnico convidado" quando não leu nada.
  const [erroLeitura, setErroLeitura] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      setLista(await osApi.tecnicosDeCampo());
      setErroLeitura(null);
    } catch (e) {
      setErroLeitura(mensagemDoErro(e, 'Não deu para ler os técnicos de campo agora.'));
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function convidar(e: FormEvent) {
    e.preventDefault();
    const novos: typeof erros = {};
    if (!nome.trim()) novos.nome = 'Escreva o nome do técnico.';
    if (!email.trim()) novos.email = 'Escreva o e-mail pessoal do técnico.';
    setErros(novos);
    if (novos.nome || novos.email) {
      document.getElementById(novos.nome ? 'convite-nome' : 'convite-email')?.focus();
      return;
    }
    setOcupado(true);
    try {
      const r = await osApi.convidarTecnico({ nome, email });
      setLista(r.tecnicos);
      aoMudar?.();
      show(
        r.emailSaiu
          ? `Convite enviado para ${email.trim()}.`
          : 'Técnico cadastrado, mas o e-mail não saiu. Use "Reenviar convite".',
        {
          variant: r.emailSaiu ? 'success' : 'error',
        }
      );
      setNome('');
      setEmail('');
      setAbrindo(false);
    } catch (err) {
      // O erro vai para o campo de e-mail: todas as recusas do servidor são sobre o e-mail (inválido, da empresa, em uso).
      setErros({ email: mensagemDoErro(err, 'Não deu para convidar agora.') });
      document.getElementById('convite-email')?.focus();
    } finally {
      setOcupado(false);
    }
  }

  async function reenviar(t: TecnicoDeCampo) {
    try {
      const r = await osApi.reenviarConvite(t.userId);
      show(
        r.emailSaiu
          ? `Convite reenviado para ${t.email}.`
          : 'O e-mail não saiu. Tente de novo em instantes.',
        {
          variant: r.emailSaiu ? 'success' : 'error',
        }
      );
    } catch (err) {
      show(mensagemDoErro(err, 'Não deu para reenviar.'), { variant: 'error' });
    }
  }

  return (
    <section
      className="flex flex-col gap-3 rounded-xl border border-line p-4 dark:border-line-dark"
      data-testid="tecnicos-de-campo"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold">Técnicos de campo (e-mail e senha)</h2>
        {!abrindo ? (
          <Button size="sm" onClick={() => setAbrindo(true)}>
            Convidar técnico
          </Button>
        ) : null}
      </div>
      <p className="text-sm text-ink-3 dark:text-ink-dark-3">
        Para quem não tem e-mail da empresa. Ele recebe um link para criar a senha e abre só o
        aplicativo do técnico.
      </p>
      {erroLeitura ? (
        <p className="text-sm text-red-deep dark:text-red-glow" data-testid="tecnicos-de-campo-erro">
          {erroLeitura}
        </p>
      ) : null}

      {abrindo ? (
        <form
          onSubmit={(e) => void convidar(e)}
          className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-start"
          noValidate
        >
          <div>
            <Label htmlFor="convite-nome">Nome</Label>
            <Input
              id="convite-nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              aria-invalid={Boolean(erros.nome)}
              aria-describedby={erros.nome ? 'convite-nome-erro' : undefined}
            />
            <ErroCampo id="convite-nome" mensagem={erros.nome} />
          </div>
          <div>
            <Label htmlFor="convite-email">E-mail pessoal</Label>
            <Input
              id="convite-email"
              type="email"
              inputMode="email"
              placeholder="nome@gmail.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={Boolean(erros.email)}
              aria-describedby={erros.email ? 'convite-email-erro' : undefined}
            />
            <ErroCampo id="convite-email" mensagem={erros.email} />
          </div>
          {/* `sm:pt-6`: os botões alinham com os campos (abaixo do rótulo), e a mensagem de erro de um campo não empurra
              o rótulo do outro (o print de 25/10 mostrou "Nome" descendo). */}
          <div className="flex gap-2 sm:pt-6">
            <Button type="submit" loading={ocupado}>
              Mandar o convite
            </Button>
            <Button type="button" variant="ghost" onClick={() => setAbrindo(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      ) : null}

      {lista && lista.length === 0 ? (
        <p className="text-sm text-ink-3 dark:text-ink-dark-3">
          Nenhum técnico convidado por e-mail ainda.
        </p>
      ) : null}
      {lista && lista.length > 0 ? (
        <ul className="flex flex-col divide-y divide-line dark:divide-line-dark">
          {lista.map((t) => (
            <li key={t.userId} className="flex flex-wrap items-center gap-2 py-2 text-sm">
              <span className="font-medium">{t.nome}</span>
              <span className="text-ink-3 dark:text-ink-dark-3">{t.email}</span>
              <Badge variant={ROTULO[t.situacao].tom}>{ROTULO[t.situacao].texto}</Badge>
              {t.situacao === 'convidado' ? (
                <Button size="sm" variant="ghost" onClick={() => void reenviar(t)}>
                  Reenviar convite
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
